/**
 * The scene as the map (specs/tki-kuutio-flow.md → Renderer): loads the map graph,
 * builds the scene and draws it, and hands every tap to the map view's existing
 * handlers with the row they expect. Nothing is re-implemented here; the view's
 * modals, data hooks and writes stay as they are, and after each write the map
 * graph is fetched again (`refreshKey`) so the whole map re-derives.
 *
 * Phase A: tap → node / root / blocker / loop modals, the ref API the view drives
 * (fit, zoom to a node, pan), proposals and new-since-seen drawn.
 * Phase B: the (+), the knot, link drawing, a label tap to retype a tree
 * connector, a tap on a link or association to retype it (SceneInteractions.jsx).
 */
import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
import styled from '@emotion/styled';
import { useTranslation } from '../../hooks/useTranslation.jsx';
import useMapGraph from '../neighbourhood/useMapGraph';
import { buildScene } from './buildScene';
import { SceneRenderer } from './SceneRenderer';
import SceneInteractions from './SceneInteractions';
import { targetOfCard, loopRow, sceneIdsFor, uuidOf } from './sceneRows';
import { viewGraph, choiceBoxes } from './sceneViews';

const CHOICE_STROKE = '#0099ff';

// What the old link handles called a card: a blocker row is 'blocker', the rest 'node'.
const linkEnd = (card) => ({ id: uuidOf(card.id), kind: card.id.startsWith('b:') ? 'blocker' : 'node' });

const SceneMap = forwardRef(function SceneMap({
  instanceId,
  refreshKey = 0,
  pathways = [],
  blockers = [],
  loops = [],
  width,
  height,
  onNodeClick = null,
  onRootClick = null,
  onBlockerClick = null,
  onLoopClick = null,
  onZoomChange = null,
  selectionIds = null, // row ids the view has selected: lit on the scene
  // Phase B: the write affordances and what they call, as the layered renderer did
  showAddButtons = false,
  showKnots = false,
  showLinkHandles = false,
  onAddConsequenceClick = null, // (node row, { x, y, svgX, svgY })
  onEdgeKnotClick = null, // ({ from, to, blockers }, { x, y })
  onEdgeLabelClick = null, // ({ from, to }, { x, y })  — a tree connector's type
  onCrossLinkTypeClick = null, // ({ from, to, effectType }, { x, y })  — a connection's type
  onDrawLink = null, // ({ id, kind }, { id, kind }, { x, y })
  onProposedLinkClick = null, // ({ from, to, effectType, reason }, { x, y })  — a proposed link, to accept or reject
  // Phase C: the map's views, as filters on the graph (sceneViews.js)
  scenarioId = null, // Skenaario / Omat: one lane and the shared targets; null is the whole map
  focus = null, // 'loops' | 'blockers' | 'cross'
  focusChain = null, // row uuids: a relation's chain, laid out as one row
  progressView = false,
  hideBlockedDownstream = false,
  showGlobalLinks = true,
  showChoices = false,
  choicePointIds = null, // row uuids of the forks (config/valence detectChoicePoints)
}, ref) {
  const { t } = useTranslation();
  const { graph, error } = useMapGraph(instanceId, refreshKey);
  const [shown, setShown] = useState(null); // the last graph that drew: a refetch never blanks the map
  useEffect(() => { if (graph) setShown(graph); }, [graph]);

  const viewed = useMemo(() => viewGraph(shown, { scenario: scenarioId, focus, chain: focusChain, progress: progressView, hideBlocked: hideBlockedDownstream, globalLinks: showGlobalLinks }),
    [shown, scenarioId, focus, focusChain, progressView, hideBlockedDownstream, showGlobalLinks]);
  const scene = useMemo(() => (viewed ? buildScene(viewed) : null), [viewed]);
  const litIds = useMemo(() => sceneIdsFor(selectionIds, scene), [selectionIds, scene]);
  const boxes = useMemo(() => (showChoices && scene ? choiceBoxes(scene, choicePointIds || []) : []), [showChoices, scene, choicePointIds]);

  // The view drives the renderer through this ref (fit, zoom to node); the map needs it
  // too: a chain or focus replaces the content wholesale, so it is framed, and on the way
  // back the person returns to where they were, not to the whole map from orbit.
  const inner = useRef(null);
  useImperativeHandle(ref, () => inner.current, [scene]);
  const viewKey = `${focusChain?.join('|') || ''}/${focus || ''}/${progressView ? 'p' : ''}/${scenarioId || ''}`;
  const before = useRef(null);
  const wasViewed = useRef(false);
  useEffect(() => {
    const now = Boolean(focusChain?.length || focus || progressView || scenarioId);
    const entering = now && !wasViewed.current;
    const leaving = !now && wasViewed.current;
    wasViewed.current = now;
    if (entering) before.current = inner.current?.getTransform?.() || null;
    const id = setTimeout(() => {
      if (leaving && before.current) { inner.current?.setTransform?.(before.current, true); before.current = null; }
      else if (now) inner.current?.fitNow?.(true);
    }, 60);
    return () => clearTimeout(id);
  }, [viewKey]); // eslint-disable-line react-hooks/exhaustive-deps

  const onCardClick = useCallback((card) => {
    const hit = targetOfCard(card, { pathways, blockers });
    if (!hit) return;
    if (hit.kind === 'root') onRootClick?.();
    else if (hit.kind === 'blocker') onBlockerClick?.(hit.row);
    else onNodeClick?.(hit.row);
  }, [pathways, blockers, onNodeClick, onRootClick, onBlockerClick]);
  const onLoop = useCallback((l) => { const row = loopRow(l, loops); if (row) onLoopClick?.(row); }, [loops, onLoopClick]);

  const onAdd = useCallback((card, pos) => {
    const hit = targetOfCard(card, { pathways, blockers });
    if (hit?.kind === 'node') onAddConsequenceClick?.(hit.row, pos);
  }, [pathways, blockers, onAddConsequenceClick]);
  const onKnot = useCallback((edge, pos) => {
    onEdgeKnotClick?.({ from: uuidOf(edge.from), to: uuidOf(edge.to), blockers: edge.blockers || [] }, pos);
  }, [onEdgeKnotClick]);
  // A proposed link is decided before it is retyped: its tap goes to the decision.
  const proposed = useCallback((edge, pos) => {
    if (edge.state !== 'proposed' || !onProposedLinkClick) return false;
    onProposedLinkClick({ from: uuidOf(edge.from), to: uuidOf(edge.to), effectType: edge.type || null, reason: edge.reason || null }, pos);
    return true;
  }, [onProposedLinkClick]);
  const onLabel = useCallback((edge, pos) => {
    if (proposed(edge, pos)) return;
    const ends = { from: uuidOf(edge.from), to: uuidOf(edge.to) };
    if (edge.kind === 'primary') onEdgeLabelClick?.(ends, pos);
    else onCrossLinkTypeClick?.({ ...ends, effectType: edge.type || null }, pos);
  }, [proposed, onEdgeLabelClick, onCrossLinkTypeClick]);
  const onEdge = useCallback((edge, pos) => {
    if (proposed(edge, pos)) return;
    onCrossLinkTypeClick?.({ from: uuidOf(edge.from), to: uuidOf(edge.to), effectType: edge.type || null }, pos);
  }, [proposed, onCrossLinkTypeClick]);
  const onLink = useCallback((fromCard, toCard, pos) => {
    if (fromCard && toCard) onDrawLink?.(linkEnd(fromCard), linkEnd(toCard), pos);
  }, [onDrawLink]);

  if (error && !shown) return <Notice>{t('impactPath.scene.error', { message: error.message || String(error) })}</Notice>;
  if (!scene) return <Notice>{t('impactPath.scene.loading')}</Notice>;
  if (!scene.cards.length) return <Notice>{t('impactPath.scene.empty')}</Notice>;
  const interactive = showAddButtons || showKnots || showLinkHandles;
  return (
    <SceneRenderer
      ref={inner}
      scene={scene}
      underlay={boxes.length ? (
        <g className="scene-choices" pointerEvents="none">
          {boxes.map((b) => (
            <g key={b.id}>
              <rect x={b.x} y={b.y} width={b.w} height={b.h} rx={16} fill="none" stroke={CHOICE_STROKE} strokeWidth={2} strokeDasharray="10 6" opacity={0.7} />
              <text x={b.x + b.w - 10} y={b.y + 18} textAnchor="end" fontSize={12} fontWeight={700} fill={CHOICE_STROKE}>{`↑${b.up} ↓${b.down}`}</text>
            </g>
          ))}
        </g>
      ) : null}
      width={width}
      height={height}
      onZoomChange={onZoomChange}
      onCardClick={onCardClick}
      onLoopClick={onLoopClick ? onLoop : null}
      litIds={litIds}
      onEdgeLabelClick={onEdgeLabelClick || onCrossLinkTypeClick || onProposedLinkClick ? onLabel : null}
      onEdgeClick={onCrossLinkTypeClick || onProposedLinkClick ? onEdge : null}
      overlay={interactive ? (
        <SceneInteractions
          scene={scene}
          showAdd={showAddButtons && Boolean(onAddConsequenceClick)}
          showKnots={showKnots && Boolean(onEdgeKnotClick)}
          showHandles={showLinkHandles && Boolean(onDrawLink)}
          onAdd={onAdd}
          onKnot={onKnot}
          onLink={onLink}
        />
      ) : null}
    />
  );
});

export default SceneMap;

const Notice = styled.div`
  position: absolute; inset: 0; display: flex; align-items: center; justify-content: center;
  color: rgba(255, 255, 255, 0.72); font-size: 16px; padding: 24px; text-align: center;
`;
